import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ConfirmationModalService } from '@epicuro-next/ataraxia/modal';
import {
  CBasketConfigurationKeys,
  CBASKET_NAMESPACE,
} from '@epicuro-next/data/baskets';
import { mapDtoStatus } from '@epicuro-next/data/baskets-grouping';
import {
  deleteBasket,
  getBasketStatusToShow,
  selectIsBasketDeletionEnabledRule,
  switchBasket,
} from '@epicuro-next/state/basket-state';
import { selectCurrentUserBasketsIds } from '@epicuro-next/state/basket-switch-state';
import { selectIsAdmin } from '@epicuro-next/state/user-state';
import { select, Store } from '@ngrx/store';
import {
  BasketSlim,
  BasketStatus,
  ReviewStatus,
} from 'graphql-api/baskets-api-interfaces';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';

const DELETE_BASKET_DIALOG_MESSAGE = $localize`:CBasket|Delete basket dialog message@@deleteBasketDialogMessage:Are you sure you want to delete basket?`;
const DELETE_BASKET_DIALOG_TITLE = $localize`:CBasket|Delete basket dialog title@@deleteBasketDialogTitle:Delete basket`;

@Component({
  selector: 'epc-basket-selector',
  templateUrl: './basket-selector.component.html',
  styleUrls: ['./basket-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BasketSelectorComponent {
  @Input() public basket: BasketSlim;
  @Input() public isActive: boolean;

  public cbasketNamespace = CBASKET_NAMESPACE;
  public basketNameFeatureKey = CBasketConfigurationKeys.EnableBasketName;

  constructor(
    private store: Store,
    private confirmationModalService: ConfirmationModalService,
  ) {}

  private isUserAdmin$ = this.store.pipe(select(selectIsAdmin));

  private currentUserBasketIds$ = this.store.pipe(
    select(selectCurrentUserBasketsIds),
  );

  private isBasketDeletionAllowed$ = this.store.pipe(
    select(selectIsBasketDeletionEnabledRule),
  );

  public canBeDeleted$ = combineLatest([
    this.isUserAdmin$,
    this.currentUserBasketIds$,
    this.isBasketDeletionAllowed$,
  ]).pipe(
    filter(
      ([, , isDeletionAllowed]) => Boolean(isDeletionAllowed) && !this.isActive,
    ),
    map(([isUserAdmin, currentUserBasketIds]) => {
      const isBasketStatusAllowed =
        this.basket.status?.toUpperCase() === BasketStatus.New;
      const isUserAllowed = currentUserBasketIds.includes(this.basket.id);

      return isUserAdmin || (isBasketStatusAllowed && isUserAllowed);
    }),
  );

  public onBasketClicked(basketId: string, event: Event) {
    event.preventDefault();
    this.store.dispatch(switchBasket({ basketId }));
  }

  public onDeleteBasket(basketId: string) {
    this.confirmationModalService
      .open({
        message: DELETE_BASKET_DIALOG_MESSAGE,
        title: DELETE_BASKET_DIALOG_TITLE,
      })
      .subscribe(() => {
        this.store.dispatch(deleteBasket({ basketId }));
      });
  }

  public getCurrency() {
    return Object.keys(this.basket.totalValueByCurrency)[0];
  }

  public getTotalValue() {
    return (this.basket.status?.toUpperCase() as BasketStatus) ===
      BasketStatus.New
      ? this.basket.totalValueByCurrency[this.getCurrency()]
      : this.basket.netTotalValueByCurrency[this.getCurrency()];
  }

  public getStoreAddresses() {
    let storeString;
    if (this.basket.basketAccounts) {
      storeString = this.basket.basketAccounts[0]?.address?.name;
      storeString =
        this.basket.basketAccounts.length > 1
          ? storeString + ` + ${this.basket.basketAccounts.length - 1} more`
          : storeString;
    }

    return storeString;
  }

  public get basketStatus(): string {
    const { reviewStatus, status } = this.basket;
    return getBasketStatusToShow(
      mapDtoStatus(status as string),
      reviewStatus as ReviewStatus,
    );
  }
}
